import React, { useRef, useState } from 'react';
import { DownloadCloud, UploadCloud, AlertTriangle, CheckCircle, Database } from 'lucide-react';
import { useDialog } from '../ui/DialogProvider.js';
import { api } from '../../services/api.js';

export const BackupRestoreView: React.FC = () => {
  const { showConfirm, showAlert } = useDialog();
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleBackup = async () => {
    try {
      setIsBackingUp(true);
      await api.downloadBackup();
      showAlert('Database backup berhasil diunduh!', { type: 'success' });
    } catch (error: any) {
      console.error(error);
      showAlert(error.message || 'Gagal melakukan backup database', { type: 'danger' });
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleRestoreClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.sql')) {
      showAlert('Format file tidak didukung. Harap unggah file SQL (.sql)', { type: 'danger' });
      return;
    }

    if (
      await showConfirm(
        'PERINGATAN BAHAYA: Proses Restore akan MENGHAPUS seluruh data Anda saat ini dan menggantinya dengan data dari file backup. Pastikan Anda sudah membuat cadangan terbaru sebelum melanjutkan. Lanjutkan proses Restore?',
        { title: 'Konfirmasi Restore Database' }
      )
    ) {
      setIsRestoring(true);
      const formData = new FormData();
      formData.append('backup_file', file);

      try {
        const result = await api.restoreDatabase(formData);
        if (result.success) {
          showAlert('Database berhasil dipulihkan dari file backup!', { type: 'success' });
          setTimeout(() => {
            window.location.reload(); // Reload to refresh all state
          }, 2000);
        } else {
          showAlert(result.message || 'Gagal memulihkan database', { type: 'danger' });
        }
      } catch (error: any) {
        console.error(error);
        showAlert(error.message || 'Terjadi kesalahan saat memulihkan database', { type: 'danger' });
      } finally {
        setIsRestoring(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    } else {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="border-b border-slate-200 bg-slate-50 p-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
            <Database className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Backup & Restore Database</h2>
            <p className="text-slate-500 mt-1 text-sm">
              Amankan seluruh data sekolah Anda dengan mengunduh salinan database secara berkala.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 grid md:grid-cols-2 gap-8">
        {/* BACKUP SECTION */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 relative overflow-hidden group hover:border-blue-300 hover:shadow-lg transition-all duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full blur-3xl -mr-10 -mt-10 opacity-60"></div>
          
          <div className="relative z-10">
            <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <DownloadCloud className="w-7 h-7" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Download Backup (SQL)</h3>
            <p className="text-slate-500 text-sm leading-relaxed mb-8">
              Unduh salinan lengkap dari seluruh tabel data sistem (Siswa, Tagihan, Transaksi, Pengaturan). Simpan file ini di tempat yang aman.
            </p>
            
            <button
              onClick={handleBackup}
              disabled={isBackingUp || isRestoring}
              className="w-full py-3.5 px-4 bg-slate-900 hover:bg-blue-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isBackingUp ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Mempersiapkan File...
                </>
              ) : (
                <>
                  <DownloadCloud className="w-5 h-5" />
                  Backup Database Sekarang
                </>
              )}
            </button>
          </div>
        </div>

        {/* RESTORE SECTION */}
        <div className="rounded-2xl border border-red-100 bg-white p-6 relative overflow-hidden group hover:border-red-300 hover:shadow-lg transition-all duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-red-50 rounded-full blur-3xl -mr-10 -mt-10 opacity-60"></div>
          
          <div className="relative z-10">
            <div className="w-14 h-14 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <UploadCloud className="w-7 h-7" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
              Restore Database
              <span className="bg-red-100 text-red-600 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full">
                Danger Zone
              </span>
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              Pulihkan sistem menggunakan file <span className="font-mono text-xs bg-slate-100 px-1 rounded">.sql</span> backup Anda. 
              <span className="block mt-1 text-red-600 font-medium">Tindakan ini akan menghapus semua data yang ada saat ini secara permanen.</span>
            </p>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".sql"
              className="hidden"
            />
            
            <button
              onClick={handleRestoreClick}
              disabled={isRestoring || isBackingUp}
              className="w-full py-3.5 px-4 bg-white hover:bg-red-50 text-red-600 border border-red-200 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isRestoring ? (
                <>
                  <div className="w-5 h-5 border-2 border-red-600/30 border-t-red-600 rounded-full animate-spin" />
                  Memulihkan Data...
                </>
              ) : (
                <>
                  <UploadCloud className="w-5 h-5" />
                  Pilih File SQL & Restore
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* SECURITY NOTICE */}
      <div className="bg-slate-50 p-5 md:p-6 border-t border-slate-200">
        <div className="flex gap-4 items-start max-w-4xl">
          <div className="w-10 h-10 shrink-0 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-semibold text-slate-800">Catatan Keamanan Penting</h4>
            <ul className="mt-2 space-y-1.5 text-sm text-slate-600">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                <span>Simpan file backup Anda di penyimpanan eksternal (Flashdisk/Google Drive) secara rutin, minimal 1x setiap akhir pekan.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                <span>Jangan mengubah isi (*edit*) file SQL menggunakan Text Editor sembarangan karena dapat merusak integritas struktur data saat di-restore.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
